import type { RequestPayload } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Divider, Group, Input, Select, Stack, Text, Textarea, Title } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import type { Coach } from "../../../models/coach";
import { getGradeOptionsType2 } from "../../../models/enums/grade";
import { syncReferenceArray } from "../../../models/IDElement";
import { emptyReportAspect, type ProbationReport, type ReportAspect } from "../../../models/reports/probationReport";
import { BaseReportForm } from "./BaseReportForm";
import type { CommonReportFormType } from "./CommonReportForm";

type ProbationFormProps = {
  probationReport: ProbationReport;
  onSubmit: (values: RequestPayload) => void;
  saveButtonLabel: string;
  coaches: Coach[];
};

type ProbationFormType = CommonReportFormType & {
  data: {
    aspects: ReportAspect[];
    remarks: string;
    discussed_at: string;
  };
};

type FormErrors = {
  title?: string[];
  status?: string[];
  data?: string[];
};

export function ProbationReportForm({ probationReport, onSubmit, saveButtonLabel, coaches }: ProbationFormProps) {
  const { errors } = usePage<{ errors: FormErrors }>().props;

  const syncedAspects = syncReferenceArray(
    emptyReportAspect,
    probationReport.report_configuration.data.aspects,
    probationReport.data.aspects,
  );

  const form = useForm<ProbationFormType>({
    mode: "uncontrolled",
    initialValues: {
      title: probationReport.title,
      status: probationReport.status,
      responsible_coach_id: probationReport.coach.id.toString(),
      data: {
        aspects: syncedAspects,
        remarks: probationReport.data.remarks,
        discussed_at: probationReport.data.discussed_at ?? null,
      },
    },
  });

  const aspectInputs = probationReport.report_configuration.data.aspects.map((aspect, index) => (
    <Stack key={aspect.id}>
      <Group>
        <Title order={6} flex={3}>
          {aspect.title}
        </Title>
        {aspect.graded && (
          <Select
            withAsterisk
            placeholder="Bewertung"
            clearable
            data={getGradeOptionsType2()}
            key={form.key(`data.aspects.${index}.grade`)}
            {...form.getInputProps(`data.aspects.${index}.grade`)}
          />
        )}
      </Group>
      {aspect.description && <Text>{aspect.description}</Text>}
      <Divider />
    </Stack>
  ));

  return (
    <BaseReportForm
      saveButtonLabel={saveButtonLabel}
      report={probationReport}
      errors={errors}
      onSubmit={form.onSubmit(onSubmit)}
      form={form}
      coaches={coaches}
    >
      <DatePickerInput
        label="Besprochen am"
        clearable
        valueFormat="DD.MM.YYYY"
        key={form.key("data.discussed_at")}
        {...form.getInputProps("data.discussed_at")}
      />
      <Input.Label>Aspekte</Input.Label>
      {aspectInputs}
      <Textarea minRows={3} label="Bemerkungen" key={form.key("data.remarks")} {...form.getInputProps("data.remarks")} />
    </BaseReportForm>
  );
}
